import axios from "axios";
import {data} from "react-router-dom";

const instance = axios.create({
    baseURL: process.env.REACT_APP_SERVER_URL + "/api/auth",
    headers: {
        "Content-Type": "application/json",
    },
});


instance.interceptors.request.use(
    (config: any) => {
        const token = getAccessToken();
        if (token) {
            config.headers["Authorization"] = "Bearer " + token;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

instance.interceptors.response.use(
    (res) => {
        return res;
    },
    async (err) => {
        const originalConfig = err.config;
        if (err.response) {
            // Validation failed, ...
            if (err.response.status === 400 && err.response.data) {
                return Promise.reject(err.response.data);
            }
            // Access Token was expired
            if (
                err.response.status === 401 &&
                !originalConfig._retry &&
                getAccessToken() != null
            ) {
                originalConfig._retry = true;
                try {
                    const rs = await refreshAccessToken();
                    const { accessToken, refreshToken } = rs.data;
                    setRefreshToken(refreshToken);
                    setAccessToken(accessToken);
                    instance.defaults.headers.common["Authorization"] =
                        "Bearer " + accessToken;
                    return instance(originalConfig);
                } catch (_error: any) {
                    if (_error.response && _error.response.data) {
                        return Promise.reject(_error.response.data);
                    }
                    return Promise.reject(_error);
                }
            }
            if (err.response.status === 403 && err.response.data) {
                return Promise.reject(err.response.data);
            }
            if (err.response.status === 404) {
                if (axios.isAxiosError(err)) {
                    return Promise.reject(err.response.data);
                }
                return;
            }
        }
        return Promise.reject(err);
    }
);

export function refreshAccessToken() {
    return instance.post("/refresh-token", {
        token: getAccessToken(),
        refreshToken: getrefreshToken(),
    });
}

export function setAccessToken(token: string) {
    window.localStorage.setItem("accessToken", token);
}

export function setRefreshToken(token: string) {
    window.localStorage.setItem("refreshToken", token);
}

export function getAccessToken(): null | string {
    return window.localStorage.getItem("accessToken");
}

export function getrefreshToken(): null | string {
    return window.localStorage.getItem("refreshToken");
}

export function getSelectedUser(): null | any {
    return window.localStorage.getItem("selectedUser");
}

export function removeTokens() {
    window.localStorage.removeItem("accessToken");
    window.localStorage.removeItem("refreshToken");
}

const responseBody: any = (response: any) => response.data;

const request = {
    get: (url: string) => instance.get(url).then().then(responseBody),
    post: (url: string, body?: any) =>
        instance.post(url, body).then().then(responseBody),
};

const User = {
    Login: (user: any) => request.post("/login", user),
    Logout: (data: any) => request.post("/logout", data),
    Register: (user: any) => request.post("/register", user),
    ConfirmEmail: (user: any) => request.post("/confirm-email", user),
    Profile: () => request.get("/profile"),
    Status: () => request.get("/status"),

    ForceLogin: (id: string) => request.post("/force-login", id),
    GetAllUsers: () => request.get("/get-all-users"),
    GetAllRoles: () => request.get("/get-all-roles"),
    AddUser: (user: any) => request.post("/add-user", user),
    DeleteUser: (id: string) => instance.delete(`/delete-user?id=${id}`).then(responseBody),
    EditUser: (user: any) => request.post("/edit-user", user),
    UpdateRoles: (id: string, roles: string[]) =>
        instance.post(`/update-roles?id=${id}`, roles).then(responseBody),
    ChangePassword: (data: any) => request.post("/change-password", data),
};

export async function Login(user: any) {
    const data = await User.Login(user)
        .then((response) => {
            return { response };
        })
        .catch((error) => {
            return error.response;
        });
    return data;
}

export async function Logout(logoutdata: any) {
    const data = await User.Logout(logoutdata)
        .then((response) => {
            return { response };
        })
        .catch((error) => {
            return error.response;
        });
    return data;
}

export async function Register(user: any) {
    const data = await User.Register(user)
        .then((response) => {
            return { response };
        })
        .catch((error) => {
            return error.response;
        });
    return data;
}

export async function ConfirmEmail(confirmData: any) {
    const data = await User.ConfirmEmail(confirmData)
        .then((response) => {
            return { response };
        })
        .catch((error) => {
            return error.response;
        });
    return data;
}

export async function Profile() {
    const data = await User.Profile()
        .then((response) => {
            return { response };
        })
        .catch((error) => {
            return error.response;
        });
    return data;
}

export async function Status() {
    const data = await User.Status()
        .then((response) => {
            return { response };
        })
        .catch((error) => {
            return error.response;
        });
    return data;
}

export function saveOldCredentials() {
    const tokens = {
        accessToken: getAccessToken(),
        refreshToken: getrefreshToken()
    }
    window.localStorage.setItem("OldCredentials", JSON.stringify(tokens));
}
export function loadOldCredentials(){
    const tokens = window.localStorage.getItem("OldCredentials");
    return tokens ? JSON.parse(tokens) : null;
}
export function removeOldCredentials(){
    window.localStorage.removeItem("OldCredentials");
}

export async function ForceLogin(user: any) {
    const data = await User.ForceLogin(user)
        .then((response) => {
            return { response };
        })
        .catch((error) => {
            return error.response;
        });
    return data;
}

export async function GetAllUsers() {
    const data = await User.GetAllUsers()
        .then((response) => ({ response }))
        .catch((error) => error.response);
    return data;
}

export async function GetAllRoles() {
    const data = await User.GetAllRoles()
        .then((response) => ({ response }))
        .catch((error) => error.response);
    return data;
}

export async function AddUser(user: any) {
    const data = await User.AddUser(user)
        .then((response) => ({ response }))
        .catch((error) => error.response);
    return data;
}

export async function DeleteUser(id: string) {
    const data = await User.DeleteUser(id)
        .then((response) => ({ response }))
        .catch((error) => error.response);
    return data;
}

export async function EditUser(user: any) {
    const data = await User.EditUser(user)
        .then((response) => ({ response }))
        .catch((error) => error.response);
    return data;
}

export async function UpdateRoles(id: string, roles: string[]) {
    const data = await User.UpdateRoles(id, roles)
        .then((response) => ({ response }))
        .catch((error) => error.response);
    return data;
}

export async function ChangePassword(passwordData: any) {
    const data = await User.ChangePassword(passwordData)
        .then((response) => ({ response }))
        .catch((error) => error.response);
    return data;
}

export function setLoggedInUser(user: any) {
    window.localStorage.setItem("loggedInUser", user);
}
